import * as React from "react";
import type { Color } from "../../lib/colors";
import { cn } from "../../lib/utils";
import { Box } from "./box";
import { glyphComponent, Icon, ICON_STROKE } from "./icon";
import type { GlyphName } from "./icon-registry";

export type TabsViewIconProps = {
  /** Glyph shown at the tab's reading edge. */
  icon: GlyphName;
  /** Solid vibrant fill of the chip; the glyph takes its paired foreground. */
  color: Color;
  /** `glyph` paints the icon directly. `compact-tile` seats a 12px glyph on
   *  a solid 14px color tile inside a 16px slot — the one tile rung every
   *  tab strip shares. `choice-tile` enlarges that treatment for a
   *  view-type choice. */
  variant?: "glyph" | "compact-tile" | "choice-tile";
  /** Ink of the glyph seated on a tile. `white` (default) in both modes, like
   *  every other solid palette fill. `ground` punches through to the page
   *  ground — white on the light ground, near-black on the dark one — for a
   *  strip that wants its tiles to read as cut-outs rather than badges.
   *  Ignored by the bare `glyph` variant. */
  ink?: "white" | "ground";
};

/**
 * A readable view-identity glyph: a colored icon, bare or seated on a solid
 * tile, for the tab that opens a view and for the menus and rosters that echo
 * it at a smaller size. Values stay opaque so the glyph stays domain-free.
 */
export function TabsViewIcon({
  icon,
  color,
  variant = "glyph",
  ink = "white",
}: TabsViewIconProps): React.ReactElement {
  if (variant === "glyph") {
    return <Icon name={icon} color={color} size="md" />;
  }

  const Glyph = glyphComponent(icon);
  const choice = variant === "choice-tile";
  return (
    <Box
      align="center"
      data-color={color}
      flex="none"
      height={choice ? 24 : 16}
      justify="center"
      width={choice ? 24 : 16}
    >
      {/* `ink="ground"` reads `--background`, so the glyph inverts with the
          mode on every fill — the one solid rung allowed to. The default white
          matches the wider palette family.
          The stroke follows the bare-icon ladder for the glyph's rendered
          size (12px = the compact rung, 20px = the prominent rung); left to
          the source default the 12px glyph drew a 0.75px line. */}
      <Box
        align="center"
        className={cn(
          "bg-c",
          ink === "ground" ? "text-background" : "text-white",
          choice ? "rounded-[5px]" : "rounded-[3px]",
        )}
        height={choice ? 24 : 14}
        justify="center"
        width={choice ? 24 : 14}
      >
        <Glyph
          aria-hidden
          className={choice ? "size-5 shrink-0" : "size-3 shrink-0"}
          color="currentColor"
          data-icon-name={icon}
          data-icon-style="salestrak-outline"
          data-slot="icon"
          size={choice ? 20 : 12}
          strokeWidth={choice ? ICON_STROKE.lg : ICON_STROKE["2xs"]}
        />
      </Box>
    </Box>
  );
}
